import { lazy, Suspense, useEffect } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { GlobalNavbarRight, PageHeader } from "@dmca/frontend/components/nav";
import {
  SmartCellThemeProvider,
  PageSettingsButton,
} from "@dmca/frontend/widgets/smartcell";
const SmartCellRFIDWidget = lazy(() =>
  import("@dmca/frontend/widgets/smartcell").then((mod) => ({
    default: mod.SmartCellRFIDWidget,
  }))
);
const SmartCellCallHelpSection = lazy(() =>
  import("@dmca/frontend/widgets/smartcell").then((mod) => ({
    default: mod.SmartCellCallHelpSection,
  }))
);
const MachineSensorGraphSection = lazy(() =>
  import("@dmca/frontend/widgets/smartcell").then((mod) => ({
    default: mod.MachineSensorGraphSection,
  }))
);
const MachineSensorStatusSection = lazy(() =>
  import("@dmca/frontend/widgets/smartcell").then((mod) => ({
    default: mod.MachineSensorStatusSection,
  }))
);
const WeldingParameterTable = lazy(() =>
  import("@dmca/frontend/widgets/welding").then((mod) => ({
    default: mod.WeldingParameterTable,
  }))
);

import { Grid, Stack } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link } from "@dmca/frontend/components/button";
import { CellFeaturesEnum } from "@dmca/models/interface/cell-location";
import {
  checkPermissionsInLoader,
  useAppPermissions,
  useLocalStorage,
} from "@dmca/frontend/hooks";

import { useState, useMemo } from "react";

import { SelectTimeframeButton } from "@dmca/frontend/components/input/datetime";
import type { DateTime } from "@dmca/utils/luxon";

export const Route = createFileRoute("/_authed/smartcell/$id")({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    await checkPermissionsInLoader("SmartCell::ViewSmartCellDetails", context);
    const { trpcQcProxy: trpc, qc } = context;
    const cells = await qc.ensureQueryData(trpc.cell.getAll.queryOptions());

    const cell = cells.find((x) => x.cellId === params.id);

    if (!cell) {
      throw notFound();
    }

    return {
      cells,
    };
  },
  head: ({ loaderData, params }) => ({
    meta: [
      {
        title: `Smart Cell Status - ${
          loaderData?.cells.find((x) => x.cellId === params.id)?.locationName
        } | DNA Dashboard`,
      },
    ],
  }),
});

function RouteComponent() {
  const { id } = Route.useParams();
  const hasPermission = useAppPermissions();

  const [selectedMachineSensorsInput, setSelectedMachineSensorsInput] =
    useLocalStorage<Array<string>>(`smartcell-selected-sensors-${id}`, []);
  const [selectedTimeRange, setSelectedTimeRange] = useState<{
    start: DateTime | number;
    end: DateTime | number;
  }>({
    start: 60 * 60 * 3,
    end: 0,
  });

  const { trpcQcProxy: trpc } = Route.useRouteContext();
  const { data: cell } = useSuspenseQuery(
    trpc.cell.getAll.queryOptions(undefined, {
      select: (data) => data.find((x) => x.cellId === id)!,
    })
  );

  const availableViewOptions = useMemo(
    () =>
      [
        cell.featureFlag.includes(CellFeaturesEnum.WELD) &&
          hasPermission("WeldingParams::ViewWeldingParams") &&
          "welding",
        cell.featureFlag.includes(CellFeaturesEnum.MACHINE_MONITORING) &&
          hasPermission("MachineMonitoring::ViewFeature") &&
          "graph",
      ].filter(Boolean) as Array<"welding" | "graph">,
    [cell.featureFlag, hasPermission]
  );
  const [selectedViews, setSelectedViews] = useLocalStorage<
    Array<"welding" | "graph">
  >(`smartcell-selected-views-${id}`, availableViewOptions);

  const { data: allMachineSensors } = useQuery(
    trpc.machineSensor.getMultiple.queryOptions(
      {
        byLocationIds: [id],
      },
      {
        enabled:
          cell?.featureFlag.includes(CellFeaturesEnum.MACHINE_MONITORING) &&
          hasPermission("MachineMonitoring::ViewFeature"),
      }
    )
  );

  useEffect(() => {
    if (!allMachineSensors?.length) return;
    if (selectedMachineSensorsInput.length === 0) {
      // If no sensors are selected, default to the first sensor
      setSelectedMachineSensorsInput([allMachineSensors[0].id]);
      return;
    }
    // Remove any selected sensors that are no longer available
    const validSelectedSensors = selectedMachineSensorsInput.filter((id) =>
      allMachineSensors.some((sensor) => sensor.id === id)
    );
    if (validSelectedSensors.length !== selectedMachineSensorsInput.length) {
      setSelectedMachineSensorsInput(validSelectedSensors);
    }
  }, [
    allMachineSensors,
    selectedMachineSensorsInput,
    setSelectedMachineSensorsInput,
  ]);

  useEffect(() => {
    // Ensure selected views are valid
    const validSelectedViews = selectedViews.filter((view) =>
      availableViewOptions.includes(view)
    );
    if (validSelectedViews.length !== selectedViews.length) {
      setSelectedViews(validSelectedViews);
    }
  }, [availableViewOptions, selectedViews, setSelectedViews]);

  const selectedMachineSensors = useMemo(() => {
    if (!allMachineSensors?.length) return [];
    return selectedMachineSensorsInput
      .map((sensorId) =>
        allMachineSensors.find((sensor) => sensor.id === sensorId)
      )
      .filter(Boolean) as typeof allMachineSensors;
  }, [selectedMachineSensorsInput, allMachineSensors]);

  const showGraphSelector = availableViewOptions.length > 1;

  return (
    <SmartCellThemeProvider>
      <PageHeader showTitleOnMobile={false}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Link
            to="/smartcell"
            search={{ plant: cell.plantId }}
            aria-label="Back to smart cell map"
            color="inherit"
          >
            <ArrowBackIcon />
          </Link>
          <span>Smart Cell Status - {cell?.locationName}</span>
        </Stack>
      </PageHeader>
      <GlobalNavbarRight>
        <SelectTimeframeButton
          times={selectedTimeRange}
          onChange={(value) => setSelectedTimeRange(value)}
        />
        {(!!allMachineSensors?.length || showGraphSelector) && (
          <PageSettingsButton
            selectedViews={selectedViews}
            setSelectedViews={setSelectedViews}
            selectedMachineSensorsInput={selectedMachineSensorsInput}
            setSelectedMachineSensorsInput={setSelectedMachineSensorsInput}
            allMachineSensors={allMachineSensors}
            availableOptions={availableViewOptions}
          />
        )}
      </GlobalNavbarRight>
      <Grid
        container
        sx={{
          "& > .MuiGrid-root": {
            maxHeight: {
              sm: "87vh",
            },
            overflow: "auto",
          },
        }}
      >
        {cell?.featureFlag?.includes(CellFeaturesEnum.RFID) &&
          hasPermission("RFID::ViewRFIDOverview") && (
            <Grid
              size={{ xs: 12, sm: 6, md: 5, lg: 4, xl: 3 }}
              sx={{
                "& > .MuiStack-root": {
                  maxHeight: "inherit",
                  "& ul": {
                    overflow: "auto",
                    maxHeight: {
                      xs: "45vh",
                      sm: "87vh",
                    },
                  },
                },
              }}
            >
              <Suspense>
                <SmartCellRFIDWidget locationID={cell.cellId} />
              </Suspense>
            </Grid>
          )}
        <Grid
          size={{ xs: "grow" }}
          sx={{
            pb: 1,
            pr: { sm: 1 },
          }}
        >
          <Stack
            direction="column"
            sx={{
              height: "100%",
              gap: 1,
              "& > *": {
                flexShrink: 0,
              },
            }}
          >
            <Stack
              direction={{ xs: "column", lg: "row" }}
              sx={{
                "& > *": { flexBasis: "50%", flexGrow: 1, height: "100%" },
                gap: 1,
              }}
            >
              {cell?.featureFlag.includes(CellFeaturesEnum.CALL_HELP) &&
                hasPermission("CallHelp::ViewFeature") && (
                  <Suspense>
                    <SmartCellCallHelpSection locationId={cell.cellId} />
                  </Suspense>
                )}

              {!!selectedMachineSensors.length && (
                <Suspense>
                  <MachineSensorStatusSection
                    device={selectedMachineSensors[0]}
                    timeRange={selectedTimeRange}
                    data-testid="machine-sensor-status-section"
                  />
                </Suspense>
              )}
            </Stack>
            {selectedViews.map((view) => {
              if (
                view === "welding" &&
                cell.featureFlag.includes(CellFeaturesEnum.WELD) &&
                hasPermission("WeldingParams::ViewWeldingParams")
              ) {
                return (
                  <Suspense key={view}>
                    <WeldingParameterTable
                      data-testid="welding-parameter-table"
                      initialPaginationState={{
                        pageIndex: 0,
                        pageSize: 8,
                      }}
                      sx={{
                        "& .MuiTablePagination-selectLabel,.MuiTablePagination-input,.MuiTablePagination-spacer":
                          {
                            display: { xs: "none", md: "inherit" },
                          },
                      }}
                    />
                  </Suspense>
                );
              }
              if (view === "graph" && !!selectedMachineSensors.length) {
                return (
                  <Suspense key={view}>
                    <MachineSensorGraphSection
                      data-testid="machine-sensor-graph-section"
                      devices={selectedMachineSensors}
                      timeRange={selectedTimeRange}
                    />
                  </Suspense>
                );
              }
              return null;
            })}
          </Stack>
        </Grid>
      </Grid>
    </SmartCellThemeProvider>
  );
}
